<template>
  <section id="abilities" class="py-12 sm:py-16 md:py-20 lg:py-24 xl:py-32 relative overflow-hidden">
    <div class="absolute inset-0 bg-gradient-to-b from-[#0b105d] to-[#0b105d]/95"></div>
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 relative z-10">
      <div class="text-center mb-10 sm:mb-12 md:mb-16">
        <h2
          class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#f4f4f4] mb-4"
          data-aos="fade-up"
        >
          {{ $t('abilities.title') }}
        </h2>
        <div
          class="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-[#fe6d0d] to-[#ffb47c] mx-auto"
          data-aos="fade-up"
          data-aos-delay="100"
        ></div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
        <div class="group relative" data-aos="fade-up" data-aos-delay="100">
          <div
            class="absolute -inset-0.5 bg-gradient-to-r from-[#0b105d] to-[#fe6d0d] rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition duration-1000 group-hover:duration-200 animate-tilt"
          ></div>
          <div
            class="relative bg-[#0f172a]/80 backdrop-blur-xl p-6 sm:p-8 rounded-2xl border border-[#ffb47c]/20 hover:border-[#ffb47c]/40 transition-all duration-300 h-full"
          >
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#fe6d0d] to-[#ffb47c] rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transform group-hover:scale-110 transition-transform"
            >
              <CloudCogIcon class="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <h3 class="text-lg sm:text-xl md:text-2xl font-bold text-[#f4f4f4] mb-3 sm:mb-4">
              {{ $t('abilities.cloudComputing.title') }}
            </h3>
            <p class="text-[#f4f4f4]/80 leading-relaxed text-sm sm:text-base">
              {{ $t('abilities.cloudComputing.description') }}
            </p>
          </div>
        </div>

        <div class="group relative" data-aos="fade-up" data-aos-delay="200">
          <div
            class="absolute -inset-0.5 bg-gradient-to-r from-[#fe6d0d] to-[#fe6d0d] rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition duration-1000 group-hover:duration-200 animate-tilt"
          ></div>
          <div
            class="relative bg-[#0f172a]/80 backdrop-blur-xl p-6 sm:p-8 rounded-2xl border border-[#ffb47c]/20 hover:border-[#ffb47c]/40 transition-all duration-300 h-full"
          >
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#0b105d] to-[#fe6d0d] rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transform group-hover:scale-110 transition-transform"
            >
              <BrainCogIcon class="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <h3 class="text-lg sm:text-xl md:text-2xl font-bold text-[#f4f4f4] mb-3 sm:mb-4">
              {{ $t('abilities.machineLearning.title') }}
            </h3>
            <p class="text-[#f4f4f4]/80 leading-relaxed text-sm sm:text-base">
              {{ $t('abilities.machineLearning.description') }}
            </p>
          </div>
        </div>

        <div
          class="group relative sm:col-span-2 lg:col-span-1"
          data-aos="fade-up"
          data-aos-delay="300"
        >
          <div
            class="absolute -inset-0.5 bg-gradient-to-r from-[#fe6d0d] to-[#0b105d] rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition duration-1000 group-hover:duration-200 animate-tilt"
          ></div>
          <div
            class="relative bg-[#0f172a]/80 backdrop-blur-xl p-6 sm:p-8 rounded-2xl border border-[#ffb47c]/20 hover:border-[#ffb47c]/40 transition-all duration-300 h-full"
          >
            <div
              class="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#ffb47c] to-[#0b105d] rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 transform group-hover:scale-110 transition-transform"
            >
              <ScalingIcon class="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
            </div>
            <h3 class="text-lg sm:text-xl md:text-2xl font-bold text-[#f4f4f4] mb-3 sm:mb-4">
              {{ $t('abilities.scalableArchitecture.title') }}
            </h3>
            <p class="text-[#f4f4f4]/80 leading-relaxed text-sm sm:text-base">
              {{ $t('abilities.scalableArchitecture.description') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { BrainCogIcon, CloudCogIcon, ScalingIcon } from 'lucide-vue-next'
</script>

<style scoped>
.animate-tilt {
  animation: tilt 10s infinite linear;
}

@keyframes tilt {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(0.5deg);
  }
  75% {
    transform: rotate(-0.5deg);
  }
}
</style>
